/**
 * @architecture-header 1
 * @layer module-infrastructure
 * @domain settings-account
 * @responsibility Serialize the SET-F02 profile-update wire command and response.
 * @state-owner authenticated API
 * @allowed-imports API client public type; @vispo/contracts
 * @forbidden React; UI role inference; email/provider adapters
 * @public-api updateProfile
 * @side-effects authenticated HTTP request
 * @tests frontend/packages/workspace-web/src/modules/settings-account/components/AccountSettingsSurface.test.tsx
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-hook banned prefixes: useGet, useFetch, useLoad, useSet, useRemove, useSave, useDo
 * @naming-path plural lowercase nouns; /me for the caller; no version, role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 */

import type { VispoApiClient } from '@vispo/api-client';
import {
  type UpdateProfileInput,
  updateProfileInputSchema,
  updateProfileResponseSchema,
} from '@vispo/contracts';

export function updateProfile(apiClient: VispoApiClient, input: UpdateProfileInput) {
  return apiClient
    .request<unknown>('/api/v1/me/profile', {
      method: 'PATCH',
      body: updateProfileInputSchema.parse(input),
    })
    .then((response) => updateProfileResponseSchema.parse(response));
}

// SET-F11: raw-octet-stream-body + header transport, not multipart/form-data -- mirrors
// upload_single_question_image, this backend's own established binary-upload precedent
// (activities/router.py), rather than introducing a new, unprecedented request shape.
export function uploadAvatar(apiClient: VispoApiClient, file: File, idempotencyKey: string) {
  return apiClient
    .request<unknown>('/api/v1/me/avatar', {
      method: 'POST',
      body: file,
      headers: {
        'Content-Type': file.type,
        'X-Idempotency-Key': idempotencyKey,
      },
    })
    .then((response) => updateProfileResponseSchema.parse(response));
}
